/**
 * Lobby Configuration for Poker Reborn
 *
 * Per-game-type configuration for the "Play Now" auto-match system.
 * Each game type (Cash, Fast-Fold, SNG) has its own PlayNowConfig
 * with independent tuning knobs.
 *
 * @see docs/screens/lobby/PLAY_NOW_PANEL.md - Play Now feature documentation
 * @see src/config/managers.ts - Manager data source (auto-generated)
 * @see src/config/lobbyCards.ts - Card descriptors shown in lobby
 * @see src/utils/managerSelection.ts - Auto-match logic using these values
 */

import type { GameType } from '@/config/managers';

// =============================================================================
// PLAY NOW CONFIGURATION (PER GAME TYPE)
// =============================================================================

/**
 * Per-game-type configuration for Play Now auto-match and auto-buyin.
 *
 * - budgetFraction: what fraction of the player's balance to use as the
 *   play budget. 0.5 = half the stack, 0.33 = a third.
 *
 *   Formula: playBudget = balance * budgetFraction
 *
 *   For Cash/Fast-Fold: finds the highest level where maxBuyin <= playBudget,
 *   then buys in at maxBuyin (full stack).
 *
 *   For SNG: finds the highest tier where buyin <= playBudget,
 *   then enters with the tier's fixed buyin.
 *
 * - defaultSeats: default seat count for Play Now auto-match.
 *   Only meaningful for Cash (where player can choose 6 or 9).
 *   Fast-Fold is always 6-max, SNG is always 9-max.
 */
export interface PlayNowConfig {
  /** Fraction of balance to use as play budget (0.5 = half, 0.33 = third). */
  readonly budgetFraction: number;
  /** Default seat count for Play Now auto-match. */
  readonly defaultSeats: 6 | 9;
}

export const PLAY_NOW_CONFIG: Record<GameType, PlayNowConfig> = {
  cash: {
    budgetFraction: 0.5,
    defaultSeats: 6,
  },
  fastfold: {
    budgetFraction: 0.5,
    defaultSeats: 6,
  },
  sng: {
    budgetFraction: 0.5,
    defaultSeats: 9,
  },
};

/** Get the Play Now config for a specific game type. */
export function getPlayNowConfig(gameType: GameType): PlayNowConfig {
  return PLAY_NOW_CONFIG[gameType];
}
